import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useApi, resolverImagen } from '../hooks/useApi';
import Lightbox from '../components/Lightbox';
import './Galeria.css';

const ESTADO_CONFIG = {
  completado: { label: 'Completado',    color: '#86efac', bg: '#14532d' },
  activo:     { label: 'En desarrollo', color: '#fcd34d', bg: '#713f12' },
  rechazado:  { label: 'Rechazado',     color: '#fca5a5', bg: '#7f1d1d' },
};
const ESTADO_VERSION = {
  aprobado:   { label: 'Aprobado para la Biblia', color: '#86efac', bg: '#14532d' },
  rechazado:  { label: 'Rechazado',               color: '#fca5a5', bg: '#7f1d1d' },
  en_proceso: { label: 'En proceso',              color: '#fcd34d', bg: '#713f12' },
};

function VersionCard({ version, index, esUltima, esAprobada, onZoom }) {
  const imgSrc = resolverImagen(version.imagen);
  return (
    <div className={`version-card${esAprobada ? ' version-aprobada' : ''}${esUltima && !esAprobada ? ' version-ultima' : ''}`}>
      <div className="version-card-header">
        <span className="version-tag">{version.version || `v${index + 1}`}</span>
        <span className="version-autor">{version.autorFicha || version.autor || '—'}</span>
        {version.estadoNorm && (
          <span className="version-estado-badge" style={{
            background: ESTADO_VERSION[version.estadoNorm]?.bg || '#292524',
            color:      ESTADO_VERSION[version.estadoNorm]?.color || '#d6d3d1',
          }}>
            {version.estado || version.estadoNorm}
          </span>
        )}
        {esAprobada && <span className="version-biblia-badge">✅ Biblia</span>}
        {esUltima && !esAprobada && <span className="version-ultima-badge">🖌️ Actual</span>}
      </div>

      {imgSrc
        ? (
          <div className="version-card-img">
            <img
              src={imgSrc}
              alt={`Versión ${version.version || index + 1}`}
              className="img-zoomable"
              onClick={() => onZoom(imgSrc)}
            />
          </div>
        )
        : <div className="version-card-no-img">Sin imagen</div>
      }

      <div className="version-card-details">
        {version.inspiracion && (
          <p className="version-detail"><strong>Inspiración:</strong> {version.inspiracion}</p>
        )}
        {version.uso && version.uso !== 'Ninguno actualmente' && version.uso !== 'Ninguno' && (
          <p className="version-detail"><strong>Uso en PDF:</strong> {version.uso}</p>
        )}
      </div>
    </div>
  );
}

function HiloCard({ hilo, onClick }) {
  const estado = ESTADO_CONFIG[hilo.estadoHilo] || ESTADO_CONFIG.activo;
  const imagen = resolverImagen(hilo.imagenPrincipal);
  return (
    <div className="hilo-card crystal-card" onClick={() => onClick(hilo)}>
      <div className="hilo-card-img">
        {imagen
          ? <img src={imagen} alt={hilo.nombre} />
          : <div className="hilo-img-placeholder">🖼️</div>
        }
        <span className="hilo-estado-badge" style={{ background: estado.bg, color: estado.color }}>
          {estado.label}
        </span>
      </div>
      <div className="hilo-card-body">
        <h3 className="hilo-nombre">{hilo.nombre}</h3>
        <p className="hilo-meta">
          {hilo.categoria && <span>{hilo.categoria}</span>}
          {hilo.autor && <span> · {hilo.autor}</span>}
        </p>
        <p className="hilo-versiones">{hilo.totalVersiones || 0} versión{hilo.totalVersiones !== 1 ? 'es' : ''}</p>
      </div>
    </div>
  );
}

function HiloDetalle({ hilo, onClose }) {
  const [lightbox, setLightbox] = useState(null);
  const estado    = ESTADO_CONFIG[hilo.estadoHilo] || ESTADO_CONFIG.activo;
  const versiones = hilo.versiones || [];
  const idxAprobada = [...versiones].map((v, i) => ({ v, i })).reverse().find(({ v }) => v.estadoNorm === 'aprobado')?.i ?? -1;
  const imgPrincipal = resolverImagen(hilo.imagenPrincipal);

  return (
    <>
      <motion.div
        className="hilo-overlay"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="hilo-detalle"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          onClick={e => e.stopPropagation()}
        >
          <button className="hilo-close" onClick={onClose}>✕</button>

          <div className="hilo-detalle-header">
            <h2 className="hilo-detalle-nombre">{hilo.nombre}</h2>
            <span className="hilo-detalle-estado" style={{ background: estado.bg, color: estado.color }}>
              {estado.label}
            </span>
          </div>

          <div className="hilo-detalle-meta">
            {hilo.categoria && <span>📂 {hilo.categoria}</span>}
            {hilo.autor     && <span>✏️ {hilo.autor}</span>}
            {hilo.hiloUrl   && <a href={hilo.hiloUrl} target="_blank" rel="noreferrer" className="hilo-discord-link">Ver en Discord →</a>}
          </div>

          {imgPrincipal && (
            <div className="hilo-img-principal-wrap">
              <img
                src={imgPrincipal}
                alt={hilo.nombre}
                className="img-zoomable"
                onClick={() => setLightbox(imgPrincipal)}
              />
              <p className="hilo-img-caption">
                {hilo.estadoHilo === 'completado' ? '✅ Versión aprobada para la Biblia' : '🖌️ Versión más reciente'}
              </p>
            </div>
          )}

          {versiones.length > 0 && (
            <div className="hilo-versiones-section">
              <h3 className="hilo-versiones-titulo">
                📜 Historial completo — {versiones.length} versión{versiones.length !== 1 ? 'es' : ''}
              </h3>
              <div className="hilo-versiones-grid">
                {versiones.map((v, i) => (
                  <VersionCard
                    key={v.messageId || i}
                    version={v}
                    index={i}
                    esUltima={i === versiones.length - 1}
                    esAprobada={i === idxAprobada}
                    onZoom={setLightbox}
                  />
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
      <Lightbox src={lightbox} alt={hilo.nombre} onClose={() => setLightbox(null)} />
    </>
  );
}

export default function Galeria() {
  const { data: hilos, loading } = useApi('/api/hilos');
  const [selected,     setSelected]     = useState(null);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroCmd,    setFiltroCmd]    = useState('');

  const filtrados = (hilos || []).filter(h => {
    const matchEstado = !filtroEstado || h.estadoHilo === filtroEstado;
    const matchCmd    = !filtroCmd    || h.comando    === filtroCmd;
    return matchEstado && matchCmd;
  });

  const comandos = [...new Set((hilos || []).map(h => h.comando))].sort();

  return (
    <div className="page galeria-page">
      <div className="container">
        <motion.div className="page-header" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <h1>Galería de Activos Visuales</h1>
          <p className="page-subtitle">Arte conceptual, sprites y diseños del proyecto Veltara</p>
        </motion.div>

        <div className="galeria-controls">
          <select className="filter-select" value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}>
            <option value="">Todos los estados</option>
            <option value="completado">Completados</option>
            <option value="activo">En desarrollo</option>
            <option value="rechazado">Rechazados</option>
          </select>
          <select className="filter-select" value={filtroCmd} onChange={e => setFiltroCmd(e.target.value)}>
            <option value="">Todas las categorías</option>
            {comandos.map(c => <option key={c} value={c}>{c.replace('ficha-', '')}</option>)}
          </select>
        </div>

        {loading && <div className="loading-rune">Cargando la galería...</div>}

        {!loading && filtrados.length === 0 && (
          <div className="empty-state">
            <span className="empty-icon">🖼️</span>
            La galería está vacía. Usa <code>!sync</code> en Discord para importar los hilos.
          </div>
        )}

        <motion.div className="galeria-grid" layout>
          <AnimatePresence>
            {filtrados.map((h, i) => (
              <motion.div key={h._id || h.discordThreadId} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.05 }}>
                <HiloCard hilo={h} onClick={setSelected} />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {selected && <HiloDetalle hilo={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </div>
  );
}